import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/layout/Navigation';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation isAuthenticated={false} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* IMPORTANT NOTICE */}
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-8">
          <h2 className="text-red-800 font-bold text-xl mb-2">⚠️ LEGAL REVIEW REQUIRED</h2>
          <p className="text-red-700">
            This is a placeholder privacy policy. A proper privacy policy must be drafted by legal
            counsel before launch, especially given COPPA requirements for users aged 14-17.
          </p>
        </div>

        <h1 className="text-4xl font-display font-bold text-rowing-gray-900 mb-8">
          Privacy Policy
        </h1>

        <div className="prose prose-lg max-w-none">
          <p className="text-rowing-gray-600 mb-6">
            <strong>Effective Date:</strong> [TO BE DETERMINED]
            <br />
            <strong>Last Updated:</strong> [TO BE DETERMINED]
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold mb-2">COPPA Notice for Parents</h3>
            <p className="text-sm">
              Glyyd collects personal information from children under 18. Parents have the right to
              review, delete, and control the collection and use of their child's information. For
              more information, see our{' '}
              <Link to="/coppa-rights" className="text-rowing-blue hover:underline">
                COPPA Rights page
              </Link>
              .
            </p>
          </div>

          <h2 className="text-2xl font-bold text-rowing-gray-900 mt-8 mb-4">
            Key Privacy Requirements to Address
          </h2>

          <h3 className="text-xl font-semibold mt-6 mb-3">1. Information We Collect</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Personal information (name, email, date of birth)</li>
            <li>Athletic performance data (ERG scores, achievements)</li>
            <li>Academic information (GPA, test scores - self-reported)</li>
            <li>Location data (for privacy law compliance)</li>
            <li>Usage data and analytics</li>
            <li>Communications between athletes and coaches</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">
            2. Age Verification & Parental Consent
          </h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Users must be 14 or older to create an account</li>
            <li>Users under 18 require parental consent</li>
            <li>Separate consent required for third-party data sharing</li>
            <li>Parents can withdraw consent at any time</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">3. How We Use Information</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>To facilitate rowing recruitment connections</li>
            <li>To verify NCAA compliance</li>
            <li>To improve our services</li>
            <li>To communicate about recruitment opportunities</li>
            <li>To ensure platform safety and security</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">4. Information Sharing</h3>
          <p className="mb-4">With parental consent, we may share information with:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>College rowing coaches and programs</li>
            <li>Rowing organizations (USRowing, IRA)</li>
            <li>NCAA compliance systems</li>
            <li>Service providers (hosting, analytics)</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">5. Parental Rights</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Access their child's personal information</li>
            <li>Request deletion of their child's data</li>
            <li>Refuse further collection or use</li>
            <li>Withdraw consent for specific data uses</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">6. Data Security</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Encryption in transit and at rest</li>
            <li>Regular security assessments</li>
            <li>Limited access controls</li>
            <li>Incident response procedures</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">7. State-Specific Rights</h3>
          <p className="mb-4">Additional rights may apply based on your location:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>California:</strong> CCPA/CPRA rights including "Do Not Sell"
            </li>
            <li>
              <strong>Other States:</strong> Various privacy rights for minors
            </li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">8. Contact Information</h3>
          <p>
            For privacy-related questions or to exercise your rights:
            <br />
            Email: privacy@glyyd.com
            <br />
            Phone: [TO BE ADDED]
            <br />
            Mail: [TO BE ADDED]
          </p>

          <div className="bg-rowing-gray-100 rounded-lg p-6 mt-8">
            <h3 className="font-semibold mb-2">Legal Counsel Note</h3>
            <p className="text-sm">
              This privacy policy must be customized to include: specific data retention periods,
              third-party processor details, international data transfer provisions, cookie details,
              marketing communications opt-out procedures, and state-specific provisions for all 50
              states.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
